// Parses Granola's "Copy" output (Markdown). Manual mode keeps the summary only: a transcript
// section is removed before storage (spec 07 §7). Every regex runs on lines of at most
// MAX_LINE_REGEX_CHARS characters (linear-time guard); longer lines are kept as plain text.
import {
  MAX_LINE_REGEX_CHARS,
  MAX_PASTE_CHARS,
  MAX_TITLE_CHARS,
  sanitizePastedText,
} from "./sanitize.ts";

export type Attendee = { name: string | null; email: string | null };
export type ParsedPaste =
  | {
      ok: true;
      title: string | null;
      summaryMarkdown: string;
      attendees: Attendee[];
      transcriptRemoved: boolean;
    }
  | { ok: false; code: "empty" | "too_large" };

export const GRANOLA_SHARE_HOSTS: readonly string[] = Object.freeze([
  "notes.granola.ai",
  "app.granola.ai",
]);
const HEADING = /^(#{1,6})[ \t]+(.*)$/;
const TRANSCRIPT_WORDS = new Set(["transcript", "transcription", "verbatim"]);
const ATTENDEES =
  /^[ \t]*(?:\*\*)?(attendees|participants|invités|invites)(?:\*\*)?[ \t]*:[ \t]*(.+)$/i;
const EMAIL = /[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,255}\.[A-Za-z]{2,24}/g;
const MAX_ATTENDEES = 50;
const MAX_EMAIL_CHARS = 254;
const ANY_HEADING_LEVEL = 7;

const isRegexSafe = (line: string) => line.length <= MAX_LINE_REGEX_CHARS;

/** Exact transcript label: the bare word, optionally bold and/or followed by a colon (no other words). */
function transcriptLabel(text: string, needsMarker: boolean): boolean {
  let s = text.trim();
  let marked = false;
  if (s.startsWith("**") || s.startsWith("__")) {
    s = s.slice(2);
    marked = true;
  }
  let end = s.length;
  while (end > 0 && " \t*_:".includes(s[end - 1])) {
    if (s[end - 1] === ":" || s[end - 1] === "*" || s[end - 1] === "_")
      marked = true;
    end--;
  }
  return (
    TRANSCRIPT_WORDS.has(s.slice(0, end).trim().toLowerCase()) &&
    (!needsMarker || marked)
  );
}

function stripTranscript(lines: string[]): {
  lines: string[];
  removed: boolean;
} {
  const out: string[] = [];
  let skipLevel = 0;
  let removed = false;
  for (const line of lines) {
    if (!isRegexSafe(line)) {
      if (!skipLevel) out.push(line);
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      const level = h[1].length;
      if (skipLevel && level <= skipLevel) skipLevel = 0;
      if (!skipLevel && transcriptLabel(h[2], false)) {
        skipLevel = level;
        removed = true;
        continue;
      }
    } else if (!skipLevel && transcriptLabel(line, true)) {
      skipLevel = ANY_HEADING_LEVEL;
      removed = true;
      continue;
    }
    if (!skipLevel) out.push(line);
  }
  return { lines: out, removed };
}

function parseAttendees(value: string): Attendee[] {
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_ATTENDEES)
    .map((part) => {
      const found = part.match(EMAIL)?.[0]?.toLowerCase() ?? null;
      // The RPCs refuse an email over 254 characters (invalid_attendees): drop it rather than the note.
      const email = found && found.length <= MAX_EMAIL_CHARS ? found : null;
      const name = part
        .replace(EMAIL, "")
        .replace(/[<>()]/g, "")
        .trim();
      return { name: name ? name.slice(0, 200) : null, email };
    });
}

function titleOf(lines: string[]): string | null {
  const first = lines.find((l) => l.trim());
  if (first === undefined) return null;
  const line = first.slice(0, MAX_LINE_REGEX_CHARS);
  const h = HEADING.exec(line);
  const text = (h ? h[2] : line.replace(/^[#>*\-\s]+/, ""))
    .replace(/\*\*/g, "")
    .trim();
  return text ? text.slice(0, MAX_TITLE_CHARS) : null;
}

export function parseGranolaPaste(raw: string): ParsedPaste {
  if (raw.length > MAX_PASTE_CHARS) return { ok: false, code: "too_large" };
  let text: string;
  try {
    text = sanitizePastedText(raw);
  } catch {
    return { ok: false, code: "too_large" };
  }
  if (!text) return { ok: false, code: "empty" };
  const { lines, removed } = stripTranscript(text.split("\n"));
  const summaryMarkdown = lines.join("\n").trim();
  if (!summaryMarkdown) return { ok: false, code: "empty" };
  let attendees: Attendee[] = [];
  for (const line of lines) {
    if (attendees.length > 0) break;
    if (!isRegexSafe(line)) continue;
    const a = ATTENDEES.exec(line);
    if (a) attendees = parseAttendees(a[2]);
  }
  return {
    ok: true,
    title: titleOf(lines),
    summaryMarkdown,
    attendees,
    transcriptRemoved: removed,
  };
}

export function validateShareUrl(
  raw: string | null | undefined,
): string | null {
  const v = (raw ?? "").trim();
  if (!v || v.length > 2000) return null;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return null;
  }
  if (
    u.protocol !== "https:" ||
    u.username ||
    u.password ||
    (u.port && u.port !== "443")
  )
    return null;
  return GRANOLA_SHARE_HOSTS.includes(u.hostname.toLowerCase()) ? u.href : null;
}
